import {
  Children,
  cloneElement,
  ComponentProps,
  DetailedReactHTMLElement,
  ReactNode,
  SyntheticEvent,
  useId,
  useRef,
  useState
} from 'react';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import cc from 'classcat';
import { ZINDEX_TOOLTIP } from 'utils/constants/app/ui';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { isMobile } from 'utils/is-mobile';
import { Portal } from 'components/Shared/Portal';
import { useClickOutside } from 'hooks/useClickOutside';
import './index.less';

if (SpringConfigs.IS_RTL) {
  import('./rtl.less');
}

type TAntDTooltipPlacement =
  | 'top'
  | 'left'
  | 'right'
  | 'bottom'
  | 'topLeft'
  | 'topRight'
  | 'bottomLeft'
  | 'bottomRight'
  | 'leftTop'
  | 'leftBottom'
  | 'rightTop'
  | 'rightBottom';

const PLACEMENT_MAPPING: Record<
  TAntDTooltipPlacement,
  ComponentProps<typeof ReactTooltip>['place']
> = {
  top: 'top',
  left: 'left',
  right: 'right',
  bottom: 'bottom',
  topLeft: 'top-start',
  topRight: 'top-end',
  bottomLeft: 'bottom-start',
  bottomRight: 'bottom-end',
  leftTop: 'left-start',
  leftBottom: 'left-end',
  rightTop: 'right-start',
  rightBottom: 'right-end'
};

type TTooltipProps = {
  title: ReactNode;
  placement?: TAntDTooltipPlacement;
  trigger?: string | string[];
  withMaxDimensions?: boolean;
  arrowPointAtCenter?: boolean;
  zIndex?: number;
  overlayClassName?: string;
  stopPropogation?: boolean;
} & PropsWithChildren &
  ComponentProps<typeof ReactTooltip>;

export const Tooltip = ({
  className = '',
  overlayClassName,
  title,
  children,
  style = {},
  withMaxDimensions = true,
  arrowPointAtCenter,
  trigger = isMobile() ? 'click' : 'hover',
  placement = 'top',
  zIndex,
  stopPropogation,
  ...restProps
}: TTooltipProps) => {
  const tooltipId = useId();

  const isClickedRef = useRef(false);

  const [isOpen, setIsOpen] = useState(false);

  const hasMoreThanOneChildren = Children.count(children) > 1;

  const shouldOpenByHover = !isMobile() && trigger?.includes('hover');
  const shouldOpenByClick = trigger?.includes('click');

  const isChildNotElement = Children.toArray(children).every(child => {
    return typeof child !== 'object';
  });

  useClickOutside(`[data-tooltip-id="${tooltipId}"]`, () => {
    setIsOpen(false);
  });

  return (
    <>
      {hasMoreThanOneChildren || isChildNotElement ? (
        <span
          style={{ display: 'flex' }}
          data-tooltip-id={tooltipId}
          className={className}
          onClick={e => {
            if (stopPropogation) {
              e.stopPropagation();
            }

            if (shouldOpenByClick) {
              isClickedRef.current = true;
              setIsOpen(prev => !prev);
            }
          }}
        >
          {children}
        </span>
      ) : (
        Children.map(children, child => {
          if (child) {
            return cloneElement(
              child as DetailedReactHTMLElement<any, HTMLElement>,
              {
                ['data-tooltip-id']: tooltipId,
                onClick: (e: SyntheticEvent) => {
                  if (shouldOpenByClick) {
                    if (stopPropogation) {
                      e.stopPropagation();
                    }

                    isClickedRef.current = true;
                    setIsOpen(prev => !prev);
                  }

                  // @ts-ignore
                  child?.props?.onClick?.(e);
                }
              }
            );
          }
        })
      )}
      <Portal>
        <ReactTooltip
          {...(shouldOpenByClick && {
            isOpen: isOpen
          })}
          setIsOpen={value => {
            if (shouldOpenByClick) {
              if (isClickedRef.current) {
                isClickedRef.current = false;

                return;
              }

              setIsOpen(value);
            }
          }}
          place={PLACEMENT_MAPPING[placement]}
          openEvents={{
            mouseover: shouldOpenByHover,
            click: shouldOpenByClick
          }}
          closeEvents={{
            mouseout: shouldOpenByHover,
            click: shouldOpenByClick
          }}
          noArrow={!arrowPointAtCenter}
          globalCloseEvents={{ scroll: true, clickOutsideAnchor: true }}
          id={tooltipId}
          className={cc([
            'SBTooltip',
            {
              [`${overlayClassName}`]: !!overlayClassName,
              ['SBTooltip--withMaxDimensions']: withMaxDimensions,
              ['react-tooltip--withMaxDimensions']: withMaxDimensions
            }
          ])}
          style={{ zIndex: zIndex || ZINDEX_TOOLTIP, ...style }}
          opacity={1}
          {...restProps}
        >
          {title}
        </ReactTooltip>
      </Portal>
    </>
  );
};
